import { Plus, Trash2 } from "lucide-react"
import { useId, useState } from "react"

import { ErrorApi } from "@/api/cliente"
import { UNIDADES, type Unidad } from "@/componentes/inventario/FormularioArticulo"
import { Boton } from "@/componentes/ui/Boton"
import { normalizarDecimal, paraElServidor } from "@/lib/decimales"
import { Campo } from "@/paginas/interno/Gastos"

/**
 * Un renglón mientras se escribe: todo texto, porque el usuario está tecleando.
 *
 * ⚠ **Lleva un `id` propio aunque todavía no esté guardado**, y no es ceremonia: con el
 * índice del array como `key`, quitar el renglón de en medio deja a React reutilizando el
 * `<input>` de la fila borrada para la siguiente, y lo escrito se corre una posición. Con
 * tres renglones y una papelera por fila, eso pasa el primer día.
 */
type Renglon = {
  id: number
  descripcion: string
  cantidad: string
  unidad: Unidad
  notas: string
}

/** Un contador por formulario: no hace falta que sea único en el mundo, sólo en la lista. */
let siguienteId = 0
const nuevo = (): Renglon => ({
  id: ++siguienteId,
  descripcion: "",
  cantidad: "1",
  unidad: "UNIDAD",
  notas: "",
})

/**
 * Pedir bienes o consumibles.
 *
 * ⚠ **No hay campo de precio ni de proveedor, y es la decisión central del módulo.**
 * Quien pide dos resmas no sabe a cuánto están ni a quién se le compran; pedírselo es la
 * fricción por la que la gente vuelve a pedir las cosas por WhatsApp. El precio aparece
 * cuando alguien redacta la orden de compra, que es otro momento y otra persona.
 *
 * ⚠ **El artículo del catálogo NO se elige aquí.** Se pide con palabras —«tóner negro, el
 * de la impresora de arriba»— y quien atiende lo empareja con el catálogo si existe. Al
 * revés, media solicitud se quedaría sin poder escribirse porque lo que hace falta todavía
 * no está dado de alta.
 *
 * ⚠ **Coma y punto valen los dos como separador decimal** (`lib/decimales.ts`): se puede
 * pedir media caja. Al servidor va normalizado con punto, que es lo único que entiende.
 */
export function FormularioSolicitud({
  onGuardar,
  guardando,
  error,
}: {
  onGuardar: (cuerpo: unknown) => void
  guardando: boolean
  error: unknown
}) {
  const id = useId()
  const [renglones, setRenglones] = useState<Renglon[]>(() => [nuevo()])
  const [prioridad, setPrioridad] = useState<"NORMAL" | "URGENTE">("NORMAL")
  const [justificacion, setJustificacion] = useState("")
  const [necesariaPara, setNecesariaPara] = useState("")
  const [entregarEn, setEntregarEn] = useState("")

  function cambiar(indice: number, campo: keyof Renglon, valor: string) {
    setRenglones((previos) => previos.map((r, i) => (i === indice ? { ...r, [campo]: valor } : r)))
  }

  const listos = renglones.filter(
    (r) => r.descripcion.trim() !== "" && normalizarDecimal(r.cantidad) !== null,
  )
  // Lo urgente dice para cuándo: lo exige el servidor y un `CHECK` de la base, así que
  // decirlo aquí evita un 409 que se lee después de haber escrito todo.
  const faltaLaFecha = prioridad === "URGENTE" && necesariaPara === ""
  const sePuede = listos.length > 0 && !faltaLaFecha && !guardando

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (!sePuede) return
        onGuardar({
          prioridad,
          justificacion: justificacion.trim() || null,
          necesaria_para: necesariaPara || null,
          entregar_en: entregarEn.trim() || null,
          lineas: listos.map((r) => ({
            descripcion: r.descripcion.trim(),
            cantidad: paraElServidor(r.cantidad),
            unidad: r.unidad,
            notas: r.notas.trim() || null,
          })),
        })
      }}
    >
      <fieldset className="space-y-3">
        <legend className="text-sm font-medium">Qué hace falta</legend>
        {renglones.map((r, i) => (
          <div key={r.id} className="grid gap-2 sm:grid-cols-[1fr_5rem_8rem_auto]">
            <input
              className="campo"
              aria-label={`Qué se pide, renglón ${i + 1}`}
              placeholder="Resmas de papel carta"
              value={r.descripcion}
              onChange={(e) => cambiar(i, "descripcion", e.target.value)}
            />
            <input
              className="campo tabular-nums"
              aria-label={`Cuánto, renglón ${i + 1}`}
              inputMode="decimal"
              value={r.cantidad}
              onChange={(e) => cambiar(i, "cantidad", e.target.value)}
            />
            {/* ⚠ **La unidad va EN EL RENGLÓN, no una sola para el pedido.** En el
                mismo pedido se piden 25 carpetas y 2 cajas de grapas: con una unidad
                común, una de las dos cifras miente. */}
            <select
              className="campo"
              aria-label={`En qué se cuenta, renglón ${i + 1}`}
              value={r.unidad}
              onChange={(e) => cambiar(i, "unidad", e.target.value)}
            >
              {UNIDADES.map((u) => (
                <option key={u.valor} value={u.valor}>
                  {u.etiqueta}
                </option>
              ))}
            </select>
            <Boton
              tamano="sm"
              aria-label={`Quitar el renglón ${i + 1}`}
              disabled={renglones.length === 1}
              onClick={() => setRenglones((p) => p.filter((otro) => otro.id !== r.id))}
            >
              <Trash2 size={16} />
            </Boton>
          </div>
        ))}
        <Boton tamano="sm" onClick={() => setRenglones((p) => [...p, nuevo()])}>
          <Plus size={16} /> Añadir otro
        </Boton>
      </fieldset>

      <Campo
        para={`${id}-justificacion`}
        etiqueta="Para qué"
        ayuda="Opcional. Ayuda a quien decide, sobre todo si es algo que no se pide a menudo."
      >
        <textarea
          id={`${id}-justificacion`}
          className="campo"
          rows={2}
          value={justificacion}
          onChange={(e) => setJustificacion(e.target.value)}
        />
      </Campo>

      <div className="grid gap-3 sm:grid-cols-2">
        <Campo para={`${id}-prioridad`} etiqueta="Prioridad">
          <select
            id={`${id}-prioridad`}
            className="campo"
            value={prioridad}
            onChange={(e) => setPrioridad(e.target.value as "NORMAL" | "URGENTE")}
          >
            <option value="NORMAL">Normal</option>
            <option value="URGENTE">Urgente</option>
          </select>
        </Campo>
        <Campo
          para={`${id}-fecha`}
          etiqueta="Para cuándo"
          ayuda={faltaLaFecha ? "Lo urgente dice para cuándo." : "Opcional."}
        >
          <input
            id={`${id}-fecha`}
            type="date"
            className="campo"
            value={necesariaPara}
            onChange={(e) => setNecesariaPara(e.target.value)}
          />
        </Campo>
      </div>

      <Campo
        para={`${id}-entregar`}
        etiqueta="Dónde se entrega"
        ayuda="Opcional. «Recepción», «Oficina 05»."
      >
        <input
          id={`${id}-entregar`}
          className="campo"
          value={entregarEn}
          onChange={(e) => setEntregarEn(e.target.value)}
        />
      </Campo>

      {error !== null && error !== undefined && (
        <p className="text-sm" role="alert" style={{ color: "var(--destructive)" }}>
          {error instanceof ErrorApi ? error.message : "No se pudo guardar."}
        </p>
      )}

      <div className="flex justify-end">
        <Boton type="submit" variante="primario" disabled={!sePuede}>
          {guardando ? "Guardando…" : "Guardar el pedido"}
        </Boton>
      </div>
      <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
        Se guarda en borrador. Hasta que no lo envíes a aprobación no lo ve nadie más.
      </p>
    </form>
  )
}
